import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, RefreshCw, Edit2, X, Link2, ExternalLink, Download, Copy, Play } from 'lucide-react'
import { useAuth } from '../../../../context/AuthContext'
import AdEditor from './AdEditor'
import {
  fetchAndParseVast,
  fireBeacon,
  servingImgUrl,
  servingClickUrl,
  servingHtmlUrl,
} from '../shared/adspeedClient'
import '../SponsorManager.css'

export default function AdDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { api } = useAuth()

  const [ad, setAd] = useState(null)
  const [mirror, setMirror] = useState(null)
  const [linkedZones, setLinkedZones] = useState([])
  const [restrictions, setRestrictions] = useState([])
  const [allZones, setAllZones] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [status, setStatus] = useState('')

  // TBTV tags state — parallel to ZoneDetail's pattern.
  const [tbtvTags, setTbtvTags] = useState([])
  const [allTbtvTags, setAllTbtvTags] = useState([])
  const [tagInput, setTagInput] = useState('')
  const [showTagSugg, setShowTagSugg] = useState(false)

  // Linkage modal state
  const [showLink, setShowLink] = useState(false)
  const [linkZoneId, setLinkZoneId] = useState('')

  // Ad-tag viewer state
  const [adtagFormat, setAdtagFormat] = useState('vast')
  const [adtag, setAdtag] = useState(null)
  const [adtagLoading, setAdtagLoading] = useState(false)

  // Live-fire state
  const [liveFireMode, setLiveFireMode] = useState(null) // 'video' | 'image' | 'html'
  const [vastFire, setVastFire] = useState(null)         // parsed VAST tracking URLs
  const [vastFireLoading, setVastFireLoading] = useState(false)
  const [vastImpressionFired, setVastImpressionFired] = useState(false)
  const liveFireKey = useRef(0) // bumped to force re-mount → re-fire

  const loadAd = useCallback(async () => {
    try {
      const data = await api(`/sponsors/ads/${id}`)
      setAd(data.ad)
      setMirror(data.mirror || null)
      setLinkedZones(data.linkedZones || [])
      setRestrictions(data.restrictions || [])
      setTbtvTags((data.tbtvTags || []).map(t => t.name))
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }, [api, id])

  const loadAllTbtvTags = useCallback(async () => {
    try {
      const data = await api('/tags')
      setAllTbtvTags(data.tags || [])
    } catch {}
  }, [api])

  const loadAllZones = useCallback(async () => {
    try {
      const data = await api('/sponsors/zones')
      setAllZones(data.zones || [])
    } catch {}
  }, [api])

  useEffect(() => {
    setLoading(true)
    Promise.all([loadAd(), loadAllZones(), loadAllTbtvTags()]).finally(() => setLoading(false))
  }, [loadAd, loadAllZones, loadAllTbtvTags])

  async function persistTbtvTags(next) {
    try {
      await api(`/sponsors/ads/${id}/tbtv-tags`, {
        method: 'PUT',
        body: JSON.stringify({ tags: next }),
      })
      setStatus('Tags saved')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
      await loadAd()
    }
  }

  function addTag(name) {
    const clean = name.trim().toLowerCase()
    if (!clean || tbtvTags.includes(clean)) return
    const next = [...tbtvTags, clean]
    setTbtvTags(next)
    setTagInput('')
    setShowTagSugg(false)
    persistTbtvTags(next)
  }

  function removeTag(name) {
    const next = tbtvTags.filter(t => t !== name)
    setTbtvTags(next)
    persistTbtvTags(next)
  }

  function handleTagKey(e) {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (tagInput.trim()) addTag(tagInput)
    } else if (e.key === 'Backspace' && !tagInput && tbtvTags.length) {
      removeTag(tbtvTags[tbtvTags.length - 1])
    }
  }

  const filteredTagSuggestions = allTbtvTags
    .filter(t => t.toLowerCase().includes(tagInput.toLowerCase()) && !tbtvTags.includes(t))
    .slice(0, 8)

  // Fetch the rendered ad-tag whenever id/format/dimensions change.
  useEffect(() => {
    if (!id || !ad) return
    const width = ad.width ?? ad.Width ?? 300
    const height = ad.height ?? ad.Height ?? 250
    setAdtagLoading(true)
    api(`/sponsors/ads/${id}/adtag?format=${adtagFormat}&width=${width}&height=${height}`)
      .then(d => {
        if (d?.ok === false) {
          setAdtag(null)
          setStatus(`Ad-tag error: ${d.error?.message || 'AdSpeed rejected'}`)
        } else {
          setAdtag(d.adtag)
        }
      })
      .catch(e => setStatus(`Ad-tag error: ${e.message}`))
      .finally(() => setAdtagLoading(false))
  }, [api, id, ad, adtagFormat])

  async function startVideoFire() {
    setVastFireLoading(true)
    setVastImpressionFired(false)
    const r = await fetchAndParseVast({ aid: id })
    if (!r.ok) {
      setStatus(`VAST player error: ${r.error}`)
      setVastFireLoading(false)
      return
    }
    setVastFire(r)
    liveFireKey.current += 1
    setLiveFireMode('video')
    setVastFireLoading(false)
  }

  function fireImpressionBeacon(url) {
    if (vastImpressionFired) return
    setVastImpressionFired(true)
    fireBeacon(url)
  }

  function openClickThrough(url) {
    if (!url) return
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  async function copyAdtag() {
    if (!adtag) return
    const text = typeof adtag === 'string'
      ? adtag
      : (adtag?.ServingCode || adtag?.Zone?.ServingCode || JSON.stringify(adtag))
    try {
      await navigator.clipboard.writeText(typeof text === 'string' ? text : JSON.stringify(text))
      setStatus('Ad-tag copied to clipboard')
    } catch {
      setStatus('Copy failed — select manually')
    }
  }

  async function handleEditSubmit(body) {
    // Only forward editable fields (not 'type' — that can't change after creation).
    const { type: _t, name, weight, status: newStatus, descr, clickurl, imageurl, width, height, ...rest } = body
    const editBody = {}
    if (name) editBody.name = name
    if (weight != null && weight !== '') editBody.weight = weight
    if (newStatus) editBody.status = newStatus
    if (descr != null) editBody.descr = descr
    if (clickurl != null) editBody.clickurl = clickurl
    if (imageurl != null) editBody.imageurl = imageurl
    if (width) editBody.width = width
    if (height) editBody.height = height
    Object.assign(editBody, rest)

    const res = await api(`/sponsors/ads/${id}`, { method: 'PATCH', body: JSON.stringify(editBody) })
    if (res?.ok === false) {
      throw new Error(res.error?.message || 'AdSpeed rejected the edit')
    }
    setEditing(false)
    setStatus('Ad updated')
    await loadAd()
  }

  async function handleLinkSubmit() {
    if (!linkZoneId) {
      setStatus('Error: pick a zone')
      return
    }
    const zone = allZones.find(z => String(z.id ?? z.ZoneId) === String(linkZoneId))
    if (!zone) {
      setStatus('Error: zone not found in cached list')
      return
    }
    try {
      const res = await api(`/sponsors/ads/${id}/link`, {
        method: 'POST',
        body: JSON.stringify({
          kind: 'zone',
          target_id: zone.id ?? zone.ZoneId,
          target_name: zone.name ?? zone.Name ?? '',
        }),
      })
      if (res?.ok === false) {
        throw new Error(res.error?.message || 'AdSpeed rejected the link')
      }
      setStatus('Linked to zone')
      setShowLink(false)
      setLinkZoneId('')
      await loadAd()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  async function handleRecoverCreative() {
    if (!confirm('Recover creative URLs from AdSpeed?\n\nFetches AdSpeed\'s serving endpoints to find this ad\'s underlying videourl / wrapperurl / imageurl. No clicks or paid impressions are registered. Results are saved locally.')) return
    try {
      const res = await api(`/sponsors/ads/${id}/recover`, { method: 'POST' })
      if (res?.ok === false) {
        throw new Error(res.error?.message || 'Could not recover')
      }
      const fields = Object.keys(res.recovered || {}).join(', ')
      setStatus(`Recovered: ${fields || '(none)'}`)
      await loadAd()
    } catch (e) {
      setStatus(`Recovery error: ${e.message}`)
    }
  }

  async function handleRecoverClickUrl() {
    if (!confirm('Recover the click destination URL?\n\n⚠ WARNING: this registers ONE click in AdSpeed\'s analytics (since AdSpeed only exposes the destination by counting a click). Inflates the ad\'s click metrics by 1. Proceed?')) return
    try {
      const res = await api(`/sponsors/ads/${id}/recover-click`, { method: 'POST' })
      if (res?.ok === false) {
        throw new Error(res.error?.message || 'Could not recover')
      }
      setStatus(`Click URL recovered: ${res.clickurl}`)
      await loadAd()
    } catch (e) {
      setStatus(`Click URL recovery error: ${e.message}`)
    }
  }

  async function handleUnlink(zone) {
    if (!confirm(`Unlink ad from zone "${zone.name ?? zone.Name}"?`)) return
    try {
      const res = await api(`/sponsors/ads/${id}/link`, {
        method: 'DELETE',
        body: JSON.stringify({
          kind: 'zone',
          target_id: zone.id ?? zone.ZoneId,
          target_name: zone.name ?? zone.Name ?? '',
        }),
      })
      if (res?.ok === false) {
        throw new Error(res.error?.message || 'AdSpeed rejected the unlink')
      }
      setStatus('Unlinked from zone')
      await loadAd()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  if (loading) {
    return (
      <div className="sponsors-page">
        <div className="muted">Loading ad…</div>
      </div>
    )
  }

  if (!ad) {
    return (
      <div className="sponsors-page">
        <button className="sponsors-refresh-btn" onClick={() => navigate('/sponsors/ads')}>
          <ArrowLeft size={14} /> Back to ads
        </button>
        <div className="sponsors-status error" style={{ marginTop: 12 }}>
          Ad {id} not found.
        </div>
      </div>
    )
  }

  // AS.Ads.getList is AdSpeed's only ad-read method — there is no
  // AS.Ad.getInfo. Returned fields: id/name/status/weight/width/height.
  // clickurl, imageurl, descr, type, etc. are SETTABLE via AS.Ad.edit but
  // AdSpeed never returns them. We mirror those locally on every vader-side
  // create/edit so the detail page can surface them too.
  const fields = [
    ['id', ad.id ?? ad.AdId ?? id],
    ['name', ad.name ?? ad.Name],
    ['status', ad.status ?? ad.Status],
    ['weight', ad.weight ?? ad.Weight],
    ['size', (ad.width && ad.height) ? `${ad.width}x${ad.height}` : '—'],
  ]

  // Pull the AdSpeed-dashboard deep-link target.
  const adspeedDashboardUrl = `https://advertiser.tenband.tv/Ads/show.html?id=${ad.id ?? ad.AdId ?? id}`

  // Build the mirror-derived field list. Empty strings filtered.
  const mirrorFields = mirror ? [
    ['ad_type',    mirror.ad_type],
    ['clickurl',   mirror.clickurl],
    ['imageurl',   mirror.imageurl],
    ['videourl',   mirror.videourl],
    ['wrapperurl', mirror.wrapperurl],
    ['pixelurl',   mirror.pixelurl],
    ['descr',      mirror.descr],
    ['skippable',  mirror.skippable],
    ['first',      mirror.first_line],
    ['next',       mirror.next_lines],
  ].filter(([, v]) => v != null && v !== '') : []

  // Filter out zones already linked.
  const linkedSet = new Set(linkedZones.map(z => String(z.id ?? z.ZoneId)))
  const linkableZones = allZones.filter(z => !linkedSet.has(String(z.id ?? z.ZoneId)))

  // Preview source: pick the best available creative URL.
  const previewVideoUrl = mirror?.videourl
  const previewImageUrl = mirror?.imageurl
  const previewWrapperUrl = mirror?.wrapperurl
  const hasPreviewable = previewVideoUrl || previewImageUrl
  const hasClickUrl = !!mirror?.clickurl

  // Infer ad family for choosing which "Fire" buttons make sense.
  // Heuristics: mirror.ad_type first (most reliable), then mirror URL presence,
  // then dimensions (1920x1080 / 1280x720 / etc. are typical video sizes).
  const adW = Number(ad.width ?? ad.Width ?? 0)
  const adH = Number(ad.height ?? ad.Height ?? 0)
  const isVideo = mirror?.ad_type === 'video' || mirror?.ad_type === 'vast'
    || !!previewVideoUrl || !!previewWrapperUrl
    || (adW >= 640 && adH >= 360 && (adW / adH).toFixed(2) >= 1.30) // common video aspect
  const isBanner = !isVideo && (mirror?.ad_type === 'banner' || !!previewImageUrl || (adW > 0 && adW < 1280))

  return (
    <div className="sponsors-page">
      <div className="sponsors-header">
        <div className="sponsors-header-left">
          <button className="sponsors-refresh-btn" onClick={() => navigate('/sponsors/ads')}>
            <ArrowLeft size={14} /> Ads
          </button>
          <h1>{ad.name ?? ad.Name ?? `Ad ${id}`}</h1>
        </div>
        <div className="sponsors-header-right">
          <a
            className="sponsors-refresh-btn"
            href={adspeedDashboardUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="Open this ad in AdSpeed's dashboard for full creative details"
          >
            <ExternalLink size={14} /> View in AdSpeed
          </a>
          <button className="sponsors-refresh-btn" onClick={loadAd}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button className="btn btn-primary" onClick={() => setEditing(true)}>
            <Edit2 size={14} /> Edit
          </button>
        </div>
      </div>

      {status && (
        <div className={`sponsors-status ${status.toLowerCase().includes('error') ? 'error' : 'success'}`}>
          <span>{status}</span>
          <button onClick={() => setStatus('')}><X size={14} /></button>
        </div>
      )}

      <div className="zone-detail-grid">
        <div>
          <div className="zone-detail-card">
            <h3>Ad properties</h3>
            {fields.map(([k, v]) => (
              <div className="zone-detail-field" key={k}>
                <span className="k">{k}</span>
                <span className="v">{v == null || v === '' ? '—' : String(v)}</span>
              </div>
            ))}
          </div>

          <div className="zone-detail-card" style={{ marginTop: 16 }}>
            <h3>
              Creative
              {mirror?.last_set_at && (
                <span className="muted" style={{ fontSize: '0.72rem', fontWeight: 'normal', marginLeft: 8 }}>
                  mirrored {mirror.last_set_at}
                </span>
              )}
            </h3>
            {mirrorFields.length === 0 ? (
              <div className="muted" style={{ fontSize: '0.85rem', marginBottom: 10 }}>
                No local creative data. AdSpeed's API doesn't return
                clickurl/imageurl/etc. for existing ads — we mirror those
                when an ad is created or edited via vader. Try recovering:
              </div>
            ) : (
              <>
                {mirrorFields.map(([k, v]) => {
                  const isUrl = /url$/i.test(k)
                  const isLong = String(v).length > 80
                  return (
                    <div className="zone-detail-field" key={k}>
                      <span className="k">{k}</span>
                      <span className="v" style={{ wordBreak: 'break-all' }}>
                        {isUrl ? (
                          <a href={v} target="_blank" rel="noopener noreferrer" style={{ color: '#f59e0b' }}>
                            {isLong ? String(v).slice(0, 80) + '…' : String(v)}
                          </a>
                        ) : (
                          String(v)
                        )}
                      </span>
                    </div>
                  )
                })}
                <div className="muted" style={{ fontSize: '0.78rem', marginTop: 8 }}>
                  Local mirror — last updated by {mirror.last_set_by || 'admin'}.
                  AdSpeed dashboard is the source of truth.
                </div>
              </>
            )}

            <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="sponsors-refresh-btn" onClick={handleRecoverCreative}>
                <Download size={13} /> Recover creative
              </button>
              {!hasClickUrl && (
                <button
                  className="sponsors-refresh-btn"
                  onClick={handleRecoverClickUrl}
                  title="Follows AdSpeed's click redirect — registers 1 click"
                >
                  <Download size={13} /> Recover click URL <span style={{ color: '#fbbf24' }}>(+1 click)</span>
                </button>
              )}
            </div>
            <div className="muted" style={{ fontSize: '0.72rem', marginTop: 6 }}>
              Note: AS.Ad.edit cannot change a video ad's videourl. Banner /
              VAST wrapper / HTML / image URLs are all editable.
            </div>
          </div>

          <div className="zone-detail-card" style={{ marginTop: 16 }}>
            <h3>Ad embed code</h3>
            <div className="adtag-format-row">
              {[
                { value: 'vast',          label: 'VAST URL' },
                { value: 'javascript',    label: 'JavaScript' },
                { value: 'iframe',        label: 'IFrame' },
                { value: 'comprehensive', label: 'Comprehensive' },
                { value: 'simplified',    label: 'Simplified' },
              ].map(f => (
                <button
                  key={f.value}
                  className={`adtag-format-btn ${adtagFormat === f.value ? 'active' : ''}`}
                  onClick={() => setAdtagFormat(f.value)}
                >
                  {f.label}
                </button>
              ))}
              <button
                className="sponsors-refresh-btn"
                onClick={copyAdtag}
                disabled={!adtag}
                style={{ marginLeft: 'auto' }}
              >
                <Copy size={13} /> Copy
              </button>
            </div>
            <div className="adtag-viewer">
              {adtagLoading ? (
                <div className="muted">Loading ad-tag…</div>
              ) : (
                <textarea
                  readOnly
                  value={typeof adtag === 'string'
                    ? adtag
                    : (adtag?.ServingCode || adtag?.Zone?.ServingCode || (adtag ? JSON.stringify(adtag, null, 2) : ''))}
                />
              )}
            </div>
            <div className="muted" style={{ fontSize: '0.72rem', marginTop: 6 }}>
              These are AdSpeed's serving-side embeds. The actual creative
              (image / video) is fetched at serve-time through{' '}
              <code>ads.tenband.tv/ad.php</code>.
            </div>
          </div>

          <div className="zone-detail-card" style={{ marginTop: 16 }}>
            <h3>Live ad-fire preview</h3>
            <div className="muted" style={{ fontSize: '0.85rem', marginBottom: 10 }}>
              Renders the ad inline using AdSpeed's serving endpoints. Loading the
              creative fires <strong>one impression</strong>. Clicking opens the
              destination URL (and registers a click).
            </div>

            {/* Mode selector */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              {isVideo && (
                <button
                  className="sponsors-refresh-btn"
                  onClick={startVideoFire}
                  disabled={vastFireLoading}
                >
                  <Play size={13} /> {vastFireLoading ? 'Loading VAST…' : 'Fire video (VAST)'}
                </button>
              )}
              {isBanner && (
                <>
                  <button
                    className="sponsors-refresh-btn"
                    onClick={() => { liveFireKey.current += 1; setLiveFireMode('image') }}
                  >
                    <Play size={13} /> Fire IMG
                  </button>
                  <button
                    className="sponsors-refresh-btn"
                    onClick={() => { liveFireKey.current += 1; setLiveFireMode('html') }}
                  >
                    <Play size={13} /> Fire HTML
                  </button>
                </>
              )}
              {liveFireMode && (
                <button
                  className="sponsors-refresh-btn"
                  onClick={() => setLiveFireMode(null)}
                  style={{ marginLeft: 'auto' }}
                >
                  <X size={13} /> Stop
                </button>
              )}
              {!isBanner && !isVideo && (
                <span className="muted" style={{ fontSize: '0.78rem' }}>
                  Couldn't infer ad type. Try recovering creative above first.
                </span>
              )}
            </div>

            {/* Rendered player */}
            {liveFireMode === 'video' && vastFire?.mediafile && (
              <div key={liveFireKey.current}>
                <video
                  controls
                  autoPlay
                  src={vastFire.mediafile}
                  onPlay={() => fireImpressionBeacon(vastFire.impression)}
                  onClick={() => openClickThrough(vastFire.clickThrough)}
                  style={{
                    width: '100%', maxHeight: 360, background: '#000',
                    borderRadius: 6, cursor: vastFire.clickThrough ? 'pointer' : 'default',
                  }}
                />
                <div className="muted" style={{ fontSize: '0.72rem', marginTop: 6 }}>
                  {vastImpressionFired
                    ? '✓ Impression fired'
                    : 'Press play to fire impression'}
                  {vastFire.duration && ` · Duration ${vastFire.duration}`}
                  {vastFire.clickThrough && ' · Click video to open destination URL'}
                </div>
              </div>
            )}

            {liveFireMode === 'image' && (
              <div key={liveFireKey.current}>
                <a
                  href={servingClickUrl({ aid: id, width: adW || 300, height: adH || 250 })}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Click to open destination URL (fires click tracking)"
                >
                  <img
                    src={servingImgUrl({ aid: id, width: adW || 300, height: adH || 250 })}
                    alt="banner ad"
                    style={{ maxWidth: '100%', maxHeight: 360, borderRadius: 6, background: '#000', cursor: 'pointer' }}
                  />
                </a>
                <div className="muted" style={{ fontSize: '0.72rem', marginTop: 6 }}>
                  ✓ Impression fired on image load. Click image to fire click tracking.
                </div>
              </div>
            )}

            {liveFireMode === 'html' && (
              <div key={liveFireKey.current}>
                <iframe
                  title="Ad HTML fire"
                  src={servingHtmlUrl({ aid: id, width: adW || 300, height: adH || 250 })}
                  width={adW || 300}
                  height={adH || 250}
                  style={{ border: '1px solid #333', background: '#000', maxWidth: '100%' }}
                  scrolling="no"
                />
                <div className="muted" style={{ fontSize: '0.72rem', marginTop: 6 }}>
                  ✓ Impression fired on iframe load. (Iframe may render blank if
                  AdSpeed enforces X-Frame-Options — use Fire IMG instead.)
                </div>
              </div>
            )}
          </div>

          {hasPreviewable && (
            <div className="zone-detail-card" style={{ marginTop: 16 }}>
              <h3>Creative preview</h3>
              {previewVideoUrl && (
                <video
                  controls
                  src={previewVideoUrl}
                  style={{ width: '100%', maxHeight: 360, background: '#000', borderRadius: 6 }}
                />
              )}
              {previewImageUrl && (
                <img
                  src={previewImageUrl}
                  alt="ad creative"
                  style={{ maxWidth: '100%', maxHeight: 360, borderRadius: 6, background: '#000' }}
                />
              )}
              {previewWrapperUrl && (
                <div className="muted" style={{ fontSize: '0.8rem', marginTop: 8 }}>
                  This is a VAST wrapper ad — preview requires a VAST-compatible player.
                  Paste this URL into one to test:
                  <div style={{ marginTop: 4 }}>
                    <code style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                      {previewWrapperUrl}
                    </code>
                  </div>
                </div>
              )}
              <div className="muted" style={{ fontSize: '0.72rem', marginTop: 8 }}>
                Previewing from the underlying creative URL — no AdSpeed
                impression registered.
              </div>
            </div>
          )}

          <div className="zone-detail-card" style={{ marginTop: 16 }}>
            <h3>Linked zones ({linkedZones.length})</h3>
            {linkedZones.length === 0 ? (
              <div className="muted" style={{ fontSize: '0.85rem' }}>
                This ad isn't linked to any zone yet. It won't be served until you link it.
              </div>
            ) : (
              <table className="zones-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {linkedZones.map(z => {
                    const zid = z.id ?? z.ZoneId
                    return (
                      <tr
                        key={zid}
                        onClick={() => navigate(`/sponsors/zones/${zid}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{zid}</td>
                        <td>{z.name ?? z.Name}</td>
                        <td>{z.status ?? z.Status}</td>
                        <td onClick={e => e.stopPropagation()}>
                          <button
                            className="sponsors-refresh-btn"
                            style={{ padding: '3px 8px', fontSize: '0.78rem' }}
                            onClick={() => handleUnlink(z)}
                          >
                            Unlink
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
            <button
              className="btn btn-primary"
              style={{ marginTop: 12 }}
              onClick={() => setShowLink(true)}
            >
              <Link2 size={14} /> Link to zone
            </button>
          </div>
        </div>

        <div>
          <div className="zone-detail-card">
            <h3>TBTV tags</h3>
            <div style={{ marginBottom: 10 }}>
              {tbtvTags.length === 0 && (
                <span className="muted" style={{ fontSize: '0.85rem' }}>No tags applied</span>
              )}
              {tbtvTags.map(t => (
                <span className="tag-chip" key={t}>
                  {t}
                  <button onClick={() => removeTag(t)} aria-label={`Remove ${t}`}>
                    <X size={11} />
                  </button>
                </span>
              ))}
            </div>
            <div className="tag-input-wrap">
              <input
                className="tag-input"
                placeholder="Add tag…"
                value={tagInput}
                onChange={e => { setTagInput(e.target.value); setShowTagSugg(true) }}
                onKeyDown={handleTagKey}
                onFocus={() => setShowTagSugg(true)}
                onBlur={() => setTimeout(() => setShowTagSugg(false), 150)}
              />
              {showTagSugg && filteredTagSuggestions.length > 0 && (
                <div className="tag-suggestions">
                  {filteredTagSuggestions.map(s => (
                    <div
                      key={s}
                      className="tag-suggestion"
                      onMouseDown={() => addTag(s)}
                    >
                      {s}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="muted" style={{ fontSize: '0.78rem', marginTop: 8 }}>
              TBTV tags are stored locally — shared with the same tag system
              used by content, zones, and ads so one tag like <code>pihl-2026</code>
              cross-references all three.
            </div>
          </div>

          <div className="zone-detail-card" style={{ marginTop: 16 }}>
            <h3>Restrictions ({restrictions.length})</h3>
            {restrictions.length === 0 ? (
              <div className="muted" style={{ fontSize: '0.85rem' }}>
                No restrictions (ad serves anywhere it's linked).
              </div>
            ) : (
              <div>
                {restrictions.map((r, i) => (
                  <div key={r.id ?? i} className="zone-detail-field">
                    <span className="k">{r.type ?? r.Type}</span>
                    <span className="v">{r.value ?? r.Value}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="muted" style={{ fontSize: '0.78rem', marginTop: 8 }}>
              Geo / time / frequency restrictions limit when this ad serves.
              (Add via AdSpeed UI for now; Phase 2 will add inline editing.)
            </div>
          </div>
        </div>
      </div>

      {editing && (
        <AdEditor
          initial={{
            name: ad.name ?? ad.Name,
            type: mirror?.ad_type || ad.type || 'banner',
            // Pre-fill creative fields from local mirror (if we have any).
            clickurl:   mirror?.clickurl,
            imageurl:   mirror?.imageurl,
            videourl:   mirror?.videourl,
            wrapperurl: mirror?.wrapperurl,
            pixelurl:   mirror?.pixelurl,
            descr:      mirror?.descr,
            skippable:  mirror?.skippable,
            status:     ad.status ?? ad.Status,
            weight:     ad.weight ?? ad.Weight,
          }}
          onClose={() => setEditing(false)}
          onSubmit={handleEditSubmit}
        />
      )}

      {showLink && (
        <div
          onClick={() => setShowLink(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#121212', border: '1px solid #333', borderRadius: 10,
              padding: 24, minWidth: 440,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: '1.2rem' }}>Link to zone</h2>
              <button onClick={() => setShowLink(false)} style={{ background: 'none', border: 'none', color: '#aaa', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>

            <label style={{ display: 'block', fontSize: '0.85rem', color: '#aaa', marginBottom: 4 }}>
              Zone
            </label>
            <select
              value={linkZoneId}
              onChange={e => setLinkZoneId(e.target.value)}
              className="zones-search"
              style={{ width: '100%', marginBottom: 16 }}
            >
              <option value="">— Pick a zone —</option>
              {linkableZones.map(z => (
                <option key={z.id ?? z.ZoneId} value={z.id ?? z.ZoneId}>
                  {(z.name ?? z.Name)} ({z.id ?? z.ZoneId})
                </option>
              ))}
            </select>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="sponsors-refresh-btn" onClick={() => setShowLink(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleLinkSubmit} disabled={!linkZoneId}>
                Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
